/**
 * /beta/redeem/:token — public redemption page.
 * 1. On mount: GET /api/beta/redeem/{token} → check validity.
 * 2. Invalid → show "no longer valid" card.
 * 3. Valid → show signup form (name, email, password, confirm).
 * 4. Submit → POST /api/beta/redeem → session cookie set server-side → redirect to /dashboard?welcome=beta.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, AlertCircle, Rocket, ArrowRight, Sparkles } from "lucide-react";
import { Brand } from "../components/Brand";
import { getJSON, postJSON } from "../lib/api";
import { useAuthStore } from "../state/authStore";
import { usePageMeta } from "../lib/usePageMeta";

const PERKS = [
    { title: "Lifetime Pro access — free, forever.",            icon: Sparkles },
    { title: "Direct line to the founding team via in-app chat.", icon: CheckCircle2 },
    { title: "Your voice shapes what we build next.",            icon: Rocket },
];

export const BetaRedeem = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuthStore();

    usePageMeta({
        title: "You've been invited to the Beta — Cybersecurity Forefront",
        description:
            "Redeem your private beta invite to Cybersecurity Forefront. Lifetime Pro access (free) + direct line to the founding team.",
    });

    const [check, setCheck] = useState({ loading: true });
    const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        getJSON(`/beta/redeem/${encodeURIComponent(token || "")}`)
            .then((d) => setCheck({ loading: false, data: d }))
            .catch((e) => setCheck({ loading: false, error: e?.message || "Network error" }));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
        if (form.password !== form.confirm) { setErr("Passwords do not match."); return; }
        setBusy(true);
        try {
            const res = await postJSON("/beta/redeem", {
                token,
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
            });
            // Refresh /api/auth/me so the global authStore has the new user
            try { await refresh(); } catch { /* non-fatal */ }
            navigate("/dashboard?welcome=beta", { replace: true });
        } catch (e) {
            setErr(e?.message || "Could not redeem this invite.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-canvas text-text-primary flex flex-col" data-testid="beta-redeem-page">
            <header className="px-5 py-4 border-b border-white/[0.05]">
                <Link to="/" className="inline-flex items-center gap-2 hover:opacity-90">
                    <Brand className="h-7 w-7" />
                    <span className="font-display font-semibold text-[14px] tracking-tight">
                        <span className="text-accent-cyan">Cyber</span>Forefront
                    </span>
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center px-5 py-12">
                <div className="w-full max-w-md">
                    {check.loading ? (
                        <div className="flex items-center gap-2 text-text-muted text-[13px]">
                            <Loader2 className="w-4 h-4 animate-spin text-accent-cyan" /> Verifying your invite…
                        </div>
                    ) : check.error ? (
                        <InvalidCard reason={check.error} />
                    ) : !check.data?.valid ? (
                        <InvalidCard reason={check.data?.status || "unknown"} />
                    ) : (
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-accent-mint/15 border border-accent-mint/40 text-accent-mint text-[11px] font-mono uppercase tracking-[0.2em] mb-4">
                                <Rocket className="w-3 h-3" /> Beta invite
                            </div>
                            <h1 className="font-display font-semibold text-3xl tracking-tight">
                                You're in.
                            </h1>
                            <p className="mt-2 text-[14px] text-text-muted leading-relaxed">
                                {check.data.label || "Private beta invitation"} — claim your lifetime Pro account below.
                            </p>

                            <ul className="mt-5 space-y-2.5">
                                {PERKS.map((p, i) => {
                                    const Icon = p.icon;
                                    return (
                                        <li key={i} className="flex items-start gap-2.5 text-[13px]">
                                            <Icon className="w-4 h-4 text-accent-mint shrink-0 mt-0.5" />
                                            <span className="text-text-primary">{p.title}</span>
                                        </li>
                                    );
                                })}
                            </ul>

                            <form onSubmit={submit} className="mt-7 space-y-3" data-testid="beta-redeem-form">
                                <LabeledInput label="Your name" value={form.name}
                                    onChange={(v) => setForm({ ...form, name: v })} testId="beta-name" autoFocus required />
                                <LabeledInput label="Email" type="email" value={form.email}
                                    onChange={(v) => setForm({ ...form, email: v })} testId="beta-email" required />
                                <LabeledInput label="Password (≥ 8 chars)" type="password" value={form.password}
                                    onChange={(v) => setForm({ ...form, password: v })} testId="beta-password" required />
                                <LabeledInput label="Confirm password" type="password" value={form.confirm}
                                    onChange={(v) => setForm({ ...form, confirm: v })} testId="beta-confirm" required />
                                {err && (
                                    <div className="text-[12.5px] text-accent-red flex items-center gap-1.5">
                                        <AlertCircle className="w-3.5 h-3.5" /> {err}
                                    </div>
                                )}
                                <button type="submit" disabled={busy}
                                    data-testid="beta-redeem-submit"
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50 transition-all">
                                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</> :
                                        <>Claim my account <ArrowRight className="w-3.5 h-3.5" /></>}
                                </button>
                                <p className="mt-2 text-[11.5px] text-text-dim text-center">
                                    By continuing you agree to our terms & privacy policy.
                                </p>
                            </form>
                        </motion.div>
                    )}
                </div>
            </main>
        </div>
    );
};

const InvalidCard = ({ reason }) => (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-accent-amber/30 bg-accent-amber/[0.05] p-6" data-testid="beta-invalid">
        <AlertCircle className="w-6 h-6 text-accent-amber" />
        <h2 className="mt-3 font-display font-semibold text-[18px]">This invite is no longer valid</h2>
        <p className="mt-2 text-[13px] text-text-muted">
            Status: <span className="font-mono text-text-primary">{reason}</span>. The link may have been used,
            revoked, or it may have expired. Reach out to the team if you think this is a mistake.
        </p>
        <div className="mt-4 flex gap-2">
            <Link to="/pricing"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan">
                Browse our pricing <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/contact"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-text-primary">
                Contact us
            </Link>
        </div>
    </motion.div>
);

const LabeledInput = ({ label, type = "text", value, onChange, testId, ...rest }) => (
    <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted">{label}</span>
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="mt-1.5 w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary focus:outline-none focus:border-accent-cyan"
            {...rest} />
    </label>
);

export default BetaRedeem;
